import { useEffect, useState, useCallback } from "react";
import usePersistedFilters from '../../hooks/usePersistedFilters'
import { useLocation } from "react-router-dom";
import {
    MdOutlineHistory,
    MdHistoryEdu,
    MdEast,
    MdWest
} from "react-icons/md";
import { useTranslation } from "react-i18next";
import moment from "moment";

import PageHeader from "../../shared/PageHeader";
import useFetchData from "../../hooks/UseFetchData";
import CustomPagination from "../../shared/CustomPagination";
import CustomTable from "../../shared/CustomTable";

const EmrHistoryPage = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const location = useLocation();
    const emr_id = location.state?.emr_id;

    const { data, loading, executeFetch } = useFetchData();

    const { filters: payloadData, setFilters: setPayloadData } = usePersistedFilters(
        'emr_history_page',
        {
            page: 1,
            perPage: 50,
            emr_id: Number(emr_id),
        }
    );

    const fetchHistoryData = useCallback(async () => {
        if (emr_id) {
            await executeFetch("POST", "/v3/emr/getEmrHistory", {
                ...payloadData,
                emr_id: Number(emr_id)
            });
        }
    }, [emr_id, payloadData, executeFetch]);

    useEffect(() => {
        fetchHistoryData();
    }, [fetchHistoryData]);

    const onPageChange = (newPage, newRowsPerPage) => {
        setPayloadData((prev) => ({
            ...prev,
            page: newPage,
            perPage: newRowsPerPage,
        }));
    };

    const getKVValue = (arr, key) => {
        if (!Array.isArray(arr)) return null;
        const item = arr.find(i => i?.Key === key);
        return item ? item.Value : null;
    };

    const kvArrayToMap = (arr) => {
        if (!Array.isArray(arr)) return {};
        const map = {};
        arr.forEach(item => {
            if (item?.Key) map[item.Key] = item.Value;
        });
        return map;
    };

    const formatSimpleValue = (val) => {
        if (val === null || val === undefined || val === "") return "---";
        if (typeof val === 'boolean') return val ? t("Yes") : t("No");
        if (typeof val === 'number') return String(val);
        if (typeof val === 'string') return val;
        return JSON.stringify(val);
    };

    const friendlyLabel = (str) => {
        if (!str) return '---';
        return str.replace(/_/g, ' ').replace(/\./g, ' › ').replace(/\b\w/g, c => c.toUpperCase());
    };

    const isKeyValueArray = (val) =>
        Array.isArray(val) && val.length > 0 &&
        val.every(item => item && typeof item === 'object' && 'Key' in item && 'Value' in item);

    const isNestedKeyValueArray = (val) =>
        Array.isArray(val) && val.length > 0 &&
        Array.isArray(val[0]) && val[0].length > 0 &&
        val[0].every(item => item && typeof item === 'object' && 'Key' in item && 'Value' in item);

    const renderValue = (val) => {
        if (val === null || val === undefined || val === "") return <span className="text-gray-300 italic">{t("empty")}</span>;
        if (typeof val === 'boolean') return <span className="font-semibold">{val ? t("Yes") : t("No")}</span>;
        if (typeof val === 'number') return <span className="font-mono">{String(val)}</span>;
        if (typeof val === 'string') {
            if (val.startsWith('<') && val.endsWith('>')) {
                const text = val.replace(/<[^>]*>/g, '').trim();
                return <span>{text || <span className="text-gray-300 italic">{t("empty")}</span>}</span>;
            }
            return <span>{val}</span>;
        }
        if (Array.isArray(val)) {
            if (val.length === 0) return <span className="text-gray-300 italic">{t("empty")}</span>;
            if (val.every(item => typeof item !== 'object')) {
                return <span>{val.join(', ')}</span>;
            }
            if (isKeyValueArray(val)) {
                return (
                    <div className="flex flex-col gap-0.5 text-[10px]">
                        {val.map((item, i) => (
                            <span key={i}>{friendlyLabel(item.Key)}: <strong>{formatSimpleValue(item.Value)}</strong></span>
                        ))}
                    </div>
                );
            }
            return <span className="font-mono text-[10px] break-all">{JSON.stringify(val)}</span>;
        }
        if (typeof val === 'object') {
            const entries = Object.entries(val);
            return (
                <div className="flex flex-col gap-0.5 text-[10px]">
                    {entries.map(([k, v], i) => (
                        <span key={i}>{friendlyLabel(k)}: <strong>{formatSimpleValue(v)}</strong></span>
                    ))}
                </div>
            );
        }
        return <span>{String(val)}</span>;
    };

    const renderDiffRow = (label, oldVal, newVal, subLabel) => (
        <div className="flex items-stretch border-b border-gray-100 last:border-b-0">
            <div className={`w-[22%] shrink-0 px-2 py-1.5 bg-gray-50/80 flex flex-col justify-center border-gray-100 ${isRTL ? 'border-l' : 'border-r'}`}>
                {subLabel && <span className="text-[9px] text-gray-400 mb-0.5">{subLabel}</span>}
                <span className="text-[10px] font-medium text-gray-700">{friendlyLabel(label)}</span>
            </div>
            <div className={`flex-1 flex items-stretch min-w-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex-1 px-2 py-1.5 bg-red-50/40 border-gray-100 flex items-center min-w-0 ${isRTL ? 'border-l' : 'border-r'}`}>
                    <div className="text-[10px] text-red-700/70 break-words min-w-0 w-full">{renderValue(oldVal)}</div>
                </div>
                <div className="flex items-center px-1 bg-gray-50/50">
                    {isRTL ? <MdWest size={10} className="text-gray-300 shrink-0" /> : <MdEast size={10} className="text-gray-300 shrink-0" />}
                </div>
                <div className="flex-1 px-2 py-1.5 bg-green-50/40 flex items-center min-w-0">
                    <div className="text-[10px] text-green-800 font-medium break-words min-w-0 w-full">{renderValue(newVal)}</div>
                </div>
            </div>
        </div>
    );

    const renderKVDiff = (oldArr, newArr) => {
        const oldMap = kvArrayToMap(oldArr);
        const newMap = kvArrayToMap(newArr);
        const allKeys = [...new Set([...Object.keys(oldMap), ...Object.keys(newMap)])];
        const changedKeys = allKeys.filter(k => {
            const o = JSON.stringify(oldMap[k] ?? null);
            const n = JSON.stringify(newMap[k] ?? null);
            return o !== n;
        });
        if (changedKeys.length === 0) return null;
        return changedKeys.map((key, i) => (
            <div key={i}>
                {renderDiffRow(key, oldMap[key], newMap[key])}
            </div>
        ));
    };

    const processChanges = (changes) => {
        if (!changes || changes.length === 0) return [];
        const groups = [];

        let currentItemGroup = null;

        changes.forEach((change) => {
            const ft = change.field_type;

            if (ft === 'item_updated') {
                const itemName = getKVValue(change.old_value, 'name') || change.field_name;
                const itemCode = getKVValue(change.old_value, 'code');
                const subChanges = [];

                if (isNestedKeyValueArray(change.new_value)) {
                    change.new_value.forEach((subArr) => {
                        subChanges.push({
                            field_name: getKVValue(subArr, 'field_name') || '---',
                            field_type: getKVValue(subArr, 'field_type') || '',
                            old_value: getKVValue(subArr, 'old_value'),
                            new_value: getKVValue(subArr, 'new_value'),
                        });
                    });
                } else if (isKeyValueArray(change.new_value)) {
                    subChanges.push({
                        field_name: getKVValue(change.new_value, 'field_name') || '---',
                        field_type: getKVValue(change.new_value, 'field_type') || '',
                        old_value: getKVValue(change.new_value, 'old_value'),
                        new_value: getKVValue(change.new_value, 'new_value'),
                    });
                }

                groups.push({
                    type: 'item_updated',
                    field_name: change.field_name,
                    itemName,
                    itemCode,
                    subChanges,
                });
            } else if (ft === 'item_added') {
                groups.push({
                    type: 'item_added',
                    field_name: change.field_name,
                    new_value: change.new_value,
                });
            } else {
                groups.push({
                    type: 'regular',
                    field_name: change.field_name,
                    field_type: ft,
                    old_value: change.old_value,
                    new_value: change.new_value,
                });
            }
        });

        return groups;
    };

    const [expandedRows, setExpandedRows] = useState({});

    const rowExpansionTemplate = (log) => {
        const groups = processChanges(log.changes);

        if (groups.length === 0) {
            return (
                <div className="px-2 py-1.5" dir={isRTL ? 'rtl' : 'ltr'}>
                    <div className="border border-gray-100 rounded bg-gray-50/50 p-4 text-center">
                        <span className="text-[10px] text-gray-400 italic">
                            {log.action === 'created'
                                ? t("Initial record creation - no prior history.")
                                : t("Manual update saved without modifying tracked fields.")}
                        </span>
                    </div>
                </div>
            );
        }

        return (
            <div className="px-2 py-1.5 space-y-1.5" dir={isRTL ? 'rtl' : 'ltr'}>
                {groups.map((group, gIdx) => {
                    if (group.type === 'regular') {
                        const isKVChange = isKeyValueArray(group.old_value) && isKeyValueArray(group.new_value);

                        return (
                            <div key={gIdx} className="border border-gray-200 rounded overflow-hidden">
                                <div className="px-2 py-1 bg-gray-100/80 border-b border-gray-200 flex items-center gap-1.5">
                                    <span className="text-[10px] font-semibold text-gray-700">{friendlyLabel(group.field_name)}</span>
                                    {group.field_type && (
                                        <span className="text-[8px] text-gray-400 bg-gray-200/60 px-1 py-0.5 rounded">{group.field_type}</span>
                                    )}
                                </div>
                                {isKVChange ? (
                                    <div>{renderKVDiff(group.old_value, group.new_value)}</div>
                                ) : (
                                    renderDiffRow(group.field_name, group.old_value, group.new_value)
                                )}
                            </div>
                        );
                    }

                    if (group.type === 'item_updated') {
                        return (
                            <div key={gIdx} className="border border-blue-200 rounded overflow-hidden">
                                <div className="px-2 py-1 bg-blue-50 border-b border-blue-200 flex items-center gap-1.5 flex-wrap">
                                    <span className="text-[9px] font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded uppercase tracking-wider">{t("Item Updated")}</span>
                                    <span className="text-[10px] font-semibold text-blue-800">{group.itemName}</span>
                                    {group.itemCode && (
                                        <span className="text-[9px] font-mono text-blue-500 bg-blue-100/70 px-1 py-0.5 rounded">{group.itemCode}</span>
                                    )}
                                </div>
                                <div>
                                    {group.subChanges.map((sub, sIdx) => (
                                        <div key={sIdx}>
                                            {renderDiffRow(sub.field_name, sub.old_value, sub.new_value)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    }

                    if (group.type === 'item_added') {
                        const kvData = isKeyValueArray(group.new_value) ? group.new_value : [];
                        return (
                            <div key={gIdx} className="border border-green-200 rounded overflow-hidden">
                                <div className="px-2 py-1 bg-green-50 border-b border-green-200 flex items-center gap-1.5 flex-wrap">
                                    <span className="text-[9px] font-bold text-green-600 bg-green-100 px-1.5 py-0.5 rounded uppercase tracking-wider">{t("Item Added")}</span>
                                    <span className="text-[10px] font-semibold text-green-800">{friendlyLabel(group.field_name)}</span>
                                </div>
                                {kvData.length > 0 ? (
                                    <div className="grid grid-cols-3 text-[10px]">
                                        {kvData.map((item, i) => {
                                            const val = item.Value;
                                            const displayVal = Array.isArray(val)
                                                ? val.map(v => typeof v === 'object' ? JSON.stringify(v) : String(v)).join(', ')
                                                : formatSimpleValue(val);
                                            const isEmpty = displayVal === '---' || displayVal === '' || displayVal === 'false' || displayVal === '0';
                                            return (
                                                <div key={i} className={`flex items-baseline gap-1 px-2 py-0.5 border-b border-gray-100 ${isRTL ? 'border-l' : 'border-r'} ${isEmpty ? 'opacity-40' : ''}`}>
                                                    <span className="text-gray-500 shrink-0 capitalize text-[9px]">{friendlyLabel(item.Key)}:</span>
                                                    <span className="text-gray-800 font-medium truncate">{displayVal}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="px-2 py-1.5">{renderValue(group.new_value)}</div>
                                )}
                            </div>
                        );
                    }

                    return null;
                })}
            </div>
        );
    };

    const columns = [
        {
            header: "",
            style: { width: "3.5rem" },
            extraProps: { expander: true }
        },
        { header: t("Patient Name"), body: (log) => <span className="text-[11px] font-medium text-gray-800">{log.patient_name || "---"}</span>, bodyCellClass: "!whitespace-normal" },
        { header: t("MRN"), body: (log) => <span className="text-[11px] font-medium text-gray-800">{log.file_number || "---"}</span> },
        // { header: t("Visit ID"), body: (log) => <span className="text-[11px] font-medium text-gray-800">{log.visit_id || "---"}</span> },
        { header: t("EMR ID"), body: (log) => <span className="text-[11px] font-medium text-gray-800">{log.emr_id || "---"}</span> },
        {
            header: t("Updated At"), body: (log) => (
                <div className="flex flex-col">
                    <span className="text-[11px] font-medium text-gray-800">{moment(log.created_at).format('MMM DD, YYYY')}</span>
                    <span className="text-[11px] font-medium text-gray-400">{moment(log.created_at).format('hh:mm A')}</span>
                </div>
            )
        },
        {
            header: t("Updated By"), body: (log) => (
                <div className="flex flex-col">
                    <span className="text-[11px] font-medium text-gray-800">{log.action_performed_by?.name || "---"}</span>
                    <span className="text-[11px] font-medium text-gray-400">{log.action_performed_by?.id}</span>
                </div>
            )
        },
        {
            header: t("Role"), body: (log) => (
                <span className="text-[11px] font-medium text-gray-800">{log.action_performed_by?.role || t("System")}</span>
            )
        },
        {
            header: t("Action"), body: (log) => {
                const isCreated = log.action === 'created';
                return (
                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded border ${isCreated
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : 'bg-blue-50 text-blue-700 border-blue-200'
                        }`}>
                        {isCreated ? t("CREATED") : t("UPDATED")}
                    </span>
                );
            }
        },
        { header: t("Reason"), body: (log) => <span className="text-[11px] font-medium text-gray-800 whitespace-normal block min-w-[100px] leading-snug">{log.action_reason || "---"}</span>, bodyCellClass: "!whitespace-normal" }
    ];

    return (
        <div className="w-full h-full bg-white !overflow-y-auto scrollbar p-4">
            <PageHeader title={t("EMR Audit History")} Icon={MdOutlineHistory} />

            <div className="w-full mt-4">
                {!emr_id ? (
                    <div className="text-center py-12 bg-gray-50 border border-gray-100 rounded">
                        <MdHistoryEdu size={48} className="mx-auto text-gray-200 mb-2" />
                        <p className="text-gray-500 font-medium">{t("Please select an EMR record to view history")}</p>
                    </div>
                ) : loading ? (
                    <div className="space-y-4">
                        <div className="h-24 bg-gray-50 rounded animate-pulse"></div>
                        {[1, 2, 3].map(i => (
                            <div key={i} className="h-32 bg-gray-50 border border-gray-100 rounded animate-pulse"></div>
                        ))}
                    </div>
                ) : (
                    <div className="w-full space-y-6">
                        <CustomTable
                            data={data?.data}
                            columns={columns}
                            isLoading={loading}
                            dataKey="id"
                            expandedRows={expandedRows}
                            onRowToggle={(e) => setExpandedRows(e.data)}
                            rowExpansionTemplate={rowExpansionTemplate}
                            rowClassName={() => "cursor-pointer"}
                            onRowClick={(e) => {
                                if (e.originalEvent.target.closest('.p-row-toggler')) return;

                                const id = e.data.id;
                                if (!id) return;
                                setExpandedRows((prev) => {
                                    if (prev && prev[id]) {
                                        return {};
                                    } else {
                                        return { [id]: true };
                                    }
                                });
                            }}
                        />

                        {data?.totalCount > payloadData.perPage && (
                            <div className="pt-4 flex justify-center pb-6 border-t border-gray-100">
                                <CustomPagination
                                    currentPage={data?.page}
                                    rowsPerPage={data?.perPage}
                                    totalRecords={data?.totalCount}
                                    totalPages={Math.ceil(data?.totalCount / payloadData.perPage)}
                                    onPageChange={onPageChange}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmrHistoryPage;
